import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Loader2, CheckCircle, AlertTriangle, Home } from "lucide-react";

export default function PayPalReturn() {
  const [location] = useLocation();
  const [, navigate] = useLocation();
  const [status, setStatus] = useState<"idle"|"processing"|"success"|"error">("idle");
  const [message, setMessage] = useState<string>("");
  const [started, setStarted] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState(0);

  const captureMutation = trpc.paypal.captureOrder.useMutation();

  const performCapture = (token: string | null, orderIdNum: number, fallbackPaypalId: string | null) => {
    setStatus("processing");
    setRetryCount(r => r + 1);
    
    console.log("[PayPal Return] Capturing order:", { orderId: orderIdNum, paypalOrderId: token || fallbackPaypalId });
    
    captureMutation.mutate({ orderId: orderIdNum, paypalOrderId: token || (fallbackPaypalId as string) }, {
      onSuccess: async (data) => {
        console.log("[PayPal Return] Capture success:", data);
        
        try {
          // Nur Warenkorb/Checkout-bezogene Keys löschen
          localStorage.removeItem("pending_order_id");
          localStorage.removeItem("pending_paypal_order_id");
          localStorage.removeItem("gallery_cart_items");
        } catch {}
        
        setStatus("success");
        setMessage("Zahlung erfolgreich abgeschlossen!");
        
        // Get download token from response
        const downloadToken = (data as any)?.downloadToken;
        
        console.log("[PayPal Return] Download token:", downloadToken);
        
        if (downloadToken) {
          // Auto-redirect nach 3 Sekunden zur gekauften Galerie
          setTimeout(() => {
            console.log("[PayPal Return] Redirecting to /purchased/" + downloadToken);
            navigate(`/purchased/${downloadToken}`);
          }, 3000);
        } else {
          // Fallback: redirect to gallery login after 3 seconds
          console.error("[PayPal Return] No download token received, redirecting to gallery login");
          setMessage("Zahlung erfolgreich, aber kein Download-Link verfügbar. Bitte kontaktieren Sie den Support.");
          setTimeout(() => {
            navigate("/gallery/login");
          }, 3000);
        }
      },
      onError: (err) => {
        console.error("[PayPal Return] Capture error:", err);
        setStatus("error");
        const errMsg = err.message || "Zahlung konnte nicht bestätigt werden. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.";
        setMessage(errMsg);
      }
    });
  };

  useEffect(() => {
    if (started) return; // prevent double execution on strict mode/re-mounts
    setStarted(true);
    const url = new URL(window.location.href);
    const token = url.searchParams.get("token"); // PayPal order ID
    const pendingOrderId = localStorage.getItem("pending_order_id");
    const fallbackPaypalId = localStorage.getItem("pending_paypal_order_id");
    
    if (!token && !fallbackPaypalId) {
      setStatus("error");
      setMessage("Keine Zahlungsreferenz gefunden. Bitte starten Sie den Checkout erneut.");
      return;
    }
    const orderIdNum = Number(pendingOrderId);
    if (!orderIdNum) {
      setStatus("error");
      setMessage("Bestellnummer ungültig. Bitte starten Sie den Checkout erneut.");
      return;
    }

    performCapture(token, orderIdNum, fallbackPaypalId);
  }, [captureMutation, navigate, started]);

  const handleRetry = () => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("token");
    const orderIdNum = Number(localStorage.getItem("pending_order_id"));
    const fallbackPaypalId = localStorage.getItem("pending_paypal_order_id");
    if (orderIdNum) {
      performCapture(token, orderIdNum, fallbackPaypalId);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-muted">
      <Card className="max-w-md w-full shadow-lg">
        <CardHeader className="text-center border-b">
          <CardTitle className="text-2xl">Zahlungsbestätigung</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6 pt-6">
          {status === "processing" && (
            <>
              <div className="flex justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
              </div>
              <div className="space-y-2">
                <p className="font-medium text-lg">Zahlung wird bestätigt...</p>
                <p className="text-sm text-muted-foreground">Bitte warten Sie, wir verarbeiten Ihre Zahlung.</p>
              </div>
            </>
          )}
          {status === "success" && (
            <>
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-lg text-green-700 dark:text-green-300">Zahlung erfolgreich!</p>
                <p className="text-sm text-muted-foreground">{message || "Ihre Bestellung wurde bestätigt."}</p>
                <p className="text-xs text-muted-foreground mt-2">Sie werden in wenigen Sekunden zu Ihren Bildern weitergeleitet...</p>
              </div>
            </>
          )}
          {status === "error" && (
            <>
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                  <AlertTriangle className="h-10 w-10 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-lg text-red-700 dark:text-red-300">Fehler bei der Zahlungsbestätigung</p>
                <p className="text-sm text-muted-foreground">{message}</p>
                {retryCount < 3 && (
                  <p className="text-xs text-muted-foreground mt-2">Versuche: {retryCount}/3</p>
                )}
              </div>
              <div className="space-y-2 pt-4">
                {retryCount < 3 && (
                  <Button onClick={handleRetry} className="w-full" variant="default">
                    Erneut versuchen
                  </Button>
                )}
                <Button onClick={() => navigate("/gallery")} className="w-full" variant="outline">
                  <Home className="h-4 w-4 mr-2" />
                  Zur Galerie
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
